import type { ReactNode } from 'react';
import {
  Body1,
  Caption1,
  Subtitle1,
  Subtitle2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { SPACE, SPACE_X } from '../styles/scale';
import { useSurfaceStyles } from '../styles/surfaces';

/**
 * A titled block on a card surface (#202) — the app's one way of saying
 * "these things belong together, and here is what they are".
 *
 * ── Why this exists ──
 * The five Details cards in `RequestForm`, the History block, Line items, the
 * reviewer's decision panel and every Admin panel each hand-rolled the same
 * three things: a card, a heading, and a gap. They disagreed on all three, and
 * on the heading LEVEL most of all — several rendered a `Subtitle2` as a bare
 * `<span>`, so a screen-reader user got a page with one heading in it. A real
 * `<h2>`/`<h3>` is not decoration; it is how the block is navigated to.
 *
 * ── THE STRUCTURAL CONTRACT, and why the layout is a grid ──
 * The heading renders as a DIRECT CHILD of the card element, always.
 * `RequestForm.detailsSections.test.tsx` (and five sibling files) resolve a card
 * as `getByText(heading, { selector: '.fui-Subtitle2' }).parentElement`, then
 * assert on that element's own ancestry. A header-row wrapper `<div>` around the
 * heading would silently re-point every one of those chains one level down and
 * fail with a message about a missing combobox rather than about the wrapper.
 *
 * That is why the card is `display: grid` rather than a stack of rows: the
 * title, the count and the action are laid out on one line by grid PLACEMENT
 * instead of by a wrapper element, so the header row costs zero DOM nodes. The
 * only wrapper in here is the one around `action`, which is an opaque
 * `ReactNode` this component cannot place directly.
 *
 * ── The count is a sibling, never part of the heading ──
 * `<h3>Line items (4)</h3>` makes the count part of the heading's accessible
 * name, so the block is announced as "Line items 4" and cannot be found by its
 * name once the number changes. It is a separate `Caption1` beside the heading.
 */

export interface SectionProps {
  /**
   * 2 → `Subtitle1` as `<h2>` (a section of the page). 3 → `Subtitle2` as
   * `<h3>` (a card inside a section). Defaults to 2; a page's own title is a
   * `Title3` `<h1>` and is never a Section.
   */
  level?: 2 | 3;
  title: string;
  /** Rendered as a separate `Caption1` — never concatenated into the heading. */
  count?: number;
  description?: ReactNode;
  /**
   * Right-aligned, on the heading's line. Subtle/transparent appearance only:
   * a primary button here competes with the page's own primary action.
   */
  action?: ReactNode;
  /** Default true. `false` for a section that is already inside a card. */
  surface?: boolean;
  children: ReactNode;
}

/**
 * Below the breakpoint every item takes the full width — Teams forbids a
 * sideways-scrolling tab canvas, and a title/count/action row is the first
 * thing to overflow one. Griffel needs a literal at-rule key, so 700px is
 * written out; `MOBILE_BREAKPOINT_PX` is the same number for anything in JS.
 */
const MOBILE = '@media (max-width: 700px)';

const useStyles = makeStyles({
  /**
   * `auto 1fr auto`: title, then the slack that pushes the action to the far
   * edge, then the action. The 1fr column is where the count sits, so a count
   * hugs its title rather than floating in the middle.
   */
  root: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    rowGap: SPACE.l,
    columnGap: SPACE_X.s,
    [MOBILE]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  title: {
    gridColumn: '1',
    minWidth: '0',
    [MOBILE]: { gridColumn: '1 / -1' },
  },
  count: {
    gridColumn: '2',
    justifySelf: 'start',
    color: tokens.colorNeutralForeground2,
    [MOBILE]: { gridColumn: '1 / -1' },
  },
  action: {
    gridColumn: '3',
    justifySelf: 'end',
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    [MOBILE]: { gridColumn: '1 / -1', justifySelf: 'start' },
  },
  description: {
    gridColumn: '1 / -1',
    color: tokens.colorNeutralForeground2,
  },
  content: { gridColumn: '1 / -1', minWidth: '0' },
});

export function Section({
  level = 2,
  title,
  count,
  description,
  action,
  surface = true,
  children,
}: SectionProps) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const Heading = level === 2 ? Subtitle1 : Subtitle2;

  return (
    <div className={mergeClasses(surface ? surfaces.card : undefined, styles.root)}>
      {/* `block` is not cosmetic: a Fluent typography preset renders an inline
          `<span>`, and `as` swaps the tag without touching that — an inline
          heading cannot hold a grid cell, so the row collapses without it. */}
      <Heading as={level === 2 ? 'h2' : 'h3'} block className={styles.title}>
        {title}
      </Heading>
      {count !== undefined && <Caption1 className={styles.count}>{count}</Caption1>}
      {action !== undefined && <div className={styles.action}>{action}</div>}
      {description !== undefined && (
        <Body1 block className={styles.description}>
          {description}
        </Body1>
      )}
      <div className={styles.content}>{children}</div>
    </div>
  );
}
